import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useMediaQuery } from "react-responsive";
import { CSSTransition } from "react-transition-group";

import Link from "../../../link";
import SVG from "../../../svg";
import { SnippetBroker } from "../../../brokers";
import CardNav from "../../card-navigation/component";
import CondtitonalTertiaryHeader from "./tertiary-header";

import * as styles from "./styles.module.scss";

const Dropdown = (props) => {
  const {
    articles,
    cta,
    handleDropdownClick,
    handleTertiaryClick,
    items,
    label,
    onClick,
    openDropdown,
    openTertiary,
    snippet
  } = props;
  const classes = classNames(styles.dropdown, {
    [styles.is_showing]: label === openDropdown
  });

  const [hasTertiary, setTertiary] = useState(false);
  const [hasCols, setCols] = useState(false);
  const [articleCards, setArticles] = useState([]);
  const [overviewCards, setOverviews] = useState([]);

  useEffect(() => {
    items && items.some((i) => i.children) && setTertiary(true);
    items && items.length > 5 && setCols(true);
  }, [items]);

  useEffect(() => {
    if (articles && articles.length > 0) {
      setOverviews(articles.filter((article) => article.alignment === "left"));
      setArticles(articles.filter((article) => article.alignment === "right"));
    }
  }, [articles]);

  const isMobileNavigation = useMediaQuery({ query: "(max-width: 1279px)" });

  return (
    <span className={classes}>
      <CSSTransition
        in={isMobileNavigation ? openDropdown === "main" || openDropdown === label : true}
        timeout={isMobileNavigation ? 350 : 0}
        classNames="subnav-header"
        unmountOnExit
      >
        <div
          className={classNames(`dropdown_trigger`, styles.dropdown_trigger, {
            [styles.dropdown_default]: !cta,
            ["btn"]: cta,
            ["btn-secondary"]: cta === "secondary",
            ["btn-primary"]: cta && cta !== "secondary"
          })}
          onKeyPress={() => handleDropdownClick(label)}
          onClick={() => handleDropdownClick(label)}
          role="button"
          tabIndex={0}
        >
          {label}
          <SVG name="angle-down" />
        </div>
      </CSSTransition>
      <CSSTransition
        in={openDropdown === label}
        timeout={isMobileNavigation ? 400 : 0}
        classNames="subnav-content"
        unmountOnExit
      >
        <div
          className={classNames(
            `dropdown_menu`,
            styles.dropdown_menu,
            snippet && styles.dropdown_menu_float
          )}
        >
          <div className={classNames(styles.dropdown_menu_container)}>
            {/* ---------------------------------------- | Overview Card, left (mobile: bottom) */}
            {overviewCards.length > 0 && (
              <div className={styles.dropdown_menu_overview}>
                {overviewCards.map((card, i) => (
                  <div key={`overview-${i}`}>
                    <CardNav {...card} />
                  </div>
                ))}
              </div>
            )}

            {/* ------------------------------------------------------ | Nav links in dropdown */}
            <div className={classNames(styles.dropdown_menu_links)}>
              <ul
                className={classNames("nav_cols", {
                  [styles.secondary_nav_cols]: hasCols,
                  [styles.tertiary_nav_group]: hasTertiary
                })}
              >
                {items &&
                  items.map((item) => {
                    return (
                      <li key={item.id} className={item.className}>
                        {/* ----------- | Determine if item is a link or a tertiary nav header */}
                        {(item.page || item.url) && (
                          <CondtitonalTertiaryHeader
                            external={item.external}
                            label={item.link_text || item.link_text}
                            item={item}
                            openTertiary={openTertiary}
                            handleTertiaryClick={handleTertiaryClick}
                            onClick={onClick}
                          />
                        )}
                        {/* ----------- | Tertiary nav content */}
                        <CSSTransition
                          in={
                            isMobileNavigation
                              ? openTertiary === item.linkText || openTertiary === item.link_text
                              : true
                          }
                          timeout={500}
                          classNames="subnav-tertiary-content"
                          unmountOnExit
                        >
                          <div>
                            {item.children &&
                              item.children.map((child) => {
                                return (
                                  <Link
                                    key={child.id}
                                    className={classNames(
                                      `nav_link`,
                                      styles.nav_link,
                                      styles.tertiary_nav_item
                                    )}
                                    label={child.linkText || child.link_text}
                                    to={child.url}
                                    onClick={child.onClick}
                                    newWindow={child.new_window}
                                  >
                                    {child.linkText || child.link_text}
                                  </Link>
                                );
                              })}
                          </div>
                        </CSSTransition>
                      </li>
                    );
                  })}
                {/* ------------------------------------------- | Where To Buy dropdown content */}
                {snippet && (
                  <li className={classNames("snippet")}>
                    <SnippetBroker {...snippet} />
                  </li>
                )}
              </ul>
            </div>

            {/* ----------------------------------------------- | Article Cards, right */}
            {articleCards.length > 0 && (
              <ul id="testing" className={classNames(styles.dropdown_menu_articles)}>
                {articleCards.map((article, i) => (
                  <li key={`article_${i}`}>
                    <CardNav outline={article.outline || "horizontal"} {...article} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </CSSTransition>
    </span>
  );
};

Dropdown.propTypes = {
  /**
   * optional array of articles, displayed after of main links
   */
  articles: PropTypes.PropTypes.arrayOf(
    PropTypes.shape({
      outline: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(["horizontal", "vertical"])]),
      image: PropTypes.shape({
        url: PropTypes.string.isRequired,
        alt: PropTypes.string
      }),
      title: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      link_text: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired
    })
  ),
  /**
   * A onClick function passed down from the Navigation component
   * A handles Secondary Nav dropdown state
   */
  handleDropdownClick: PropTypes.func,
  /**
   * A onClick function passed down from the Navigation component
   * Handles TertiaryNav dropdown state
   */
  handleTertiaryClick: PropTypes.func,
  /**
   * Element(s) to render to the screen
   */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      children: PropTypes.array,
      id: PropTypes.string,
      link_text: PropTypes.string,
      linkText: PropTypes.string,
      page: PropTypes.object,
      thumb: PropTypes.object,
      url: PropTypes.string
    })
  ),
  /**
   * Dropdown label
   */
  label: PropTypes.string.isRequired,
  /**
   * A onClick function passed down from the navigation component
   */
  onClick: PropTypes.func,
  /**
   * Name of the secondary dropdown that is currently open
   */
  openDropdown: PropTypes.string,
  /**
   * Name of the tertiary dropdown that is currently open
   */
  openTertiary: PropTypes.string,
  /**
   * Optional data to render a snippet
   */
  snippet: PropTypes.object,
  cta: PropTypes.bool
};

export default Dropdown;
